import { AuthContext } from "@/context/AuthContext";
import ConductionAPI, { FileResponse, LiveEventProjectLists } from "@/javascript/conduction-api";
import { Project } from "@/javascript/project-objects";
import { Utils } from "@/javascript/utils";
import ConductionModal from "@/components/modals/ConductionModal";
import {
	Button,
	Flex,
	ScrollArea,
	Skeleton,
	Table,
	Text
} from "@mantine/core";
import { IconAlertCircle, IconCheck } from "@tabler/icons-react";
import { useCallback, useContext, useEffect, useState } from "react";

interface LiveEventInstrumentalistInterfaceProps {
	liveEventId: string;
	onClose: () => void;
}

const LiveEventInstrumentalistInterface: React.FC<LiveEventInstrumentalistInterfaceProps> = ({ liveEventId, onClose }) => {
	const [projectPerformanceQueue, setProjectPerformanceQueue] = useState<Project[] | null>(null);
	const [projectPerformanceQueuePosition, setProjectPerformanceQueuePosition] = useState<number | null>(null);

	const auth = useContext(AuthContext);
	if (auth === undefined) {
		throw new Error("This component must be a child of AuthContext.");
	}
	const token = auth.token;
	if (token === null) {
		return;
	}

	const updateProjectLists = (projectLists: LiveEventProjectLists | null) => {
		if (!projectLists) {
			setProjectPerformanceQueue([]);
			setProjectPerformanceQueuePosition(null);
		} else {
			const { projectPerformanceQueue, projectPerformanceQueuePosition } = projectLists || {};
			setProjectPerformanceQueue(projectPerformanceQueue);
			setProjectPerformanceQueuePosition(projectPerformanceQueuePosition);
		}
	}

	const handleDownloadScoreParts = useCallback((project: Project) => {
		const notificationId = Utils.displayNotification(
			"Fetching Project Parts",
			"Your download will begin once the export process completes. This may take a few minutes.",
			"blue",
			"loading"
		);
		ConductionAPI.getProjectInstrumentPartPdfs(project.getId(), token)
			.then((response: FileResponse) => {
				const link = document.createElement("a");
				link.href = response.fileBlobUrl;
				link.download = `${project.getName()}.zip`;
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				window.URL.revokeObjectURL(response.fileBlobUrl);

				Utils.updateNotification(
					notificationId,
					"Export Complete",
					`The generated instrument parts will now download in a ZIP file.`,
					"green",
					<IconCheck size={16} />
				);
			})
			.catch(() => {
				Utils.updateNotification(
					notificationId,
					"Error Fetching Project Parts",
					`An error occurred while fetching the project parts for ${project.getName()}.`,
					"red",
					<IconAlertCircle size={16} />,
					true,
					false
				);
			});
	}, [liveEventId, token]);

	const fetchProjectLists = useCallback(() => {
		ConductionAPI.getLiveEventProjectLists(liveEventId, token)
			.then((projectLists: LiveEventProjectLists | null) => {
				updateProjectLists(projectLists);
			})
			.catch(() => {
				Utils.displayNotification(
					"Error Fetching Projects",
					"An error occurred while retrieving live event projects.",
					"red",
					<IconAlertCircle size={16} />,
					true
				);
			});
	}, [liveEventId, token]);

	useEffect(() => {
		fetchProjectLists();
		const fetchLoop = setInterval(() => {
			fetchProjectLists();
		}, 10000); // Fetch every 10 seconds
		return () => {
			clearInterval(fetchLoop);
		}
	}, []);

	return (
		<ConductionModal
			title="Live Event Queue"
			onClose={onClose}
			size={"xl"}
		>
			<Flex
				direction={"row"}
				justify="stretch"
				style={{
					height: `95vh`
				}}
			>
				{projectPerformanceQueue === null ? (
					<Skeleton height={"100%"} radius="md" />
				) : projectPerformanceQueue.length === 0 ? (
					<Text>No projects in the performance queue.</Text>
				) : (
					<ScrollArea
						style={{
							width: "100%"
						}}
					>
						<Table
							striped
							stickyHeader
							highlightOnHover
						>
							<Table.Thead>
								<Table.Tr>
									<Table.Th>Project Name</Table.Th>
									<Table.Th>Creator</Table.Th>
								</Table.Tr>
							</Table.Thead>
							<Table.Tbody>
								{projectPerformanceQueue.map((project, index) => {
									const projectIsAtQueuePosition = projectPerformanceQueuePosition !== null ? projectPerformanceQueuePosition === index : false;
									const projectIsPastQueuePosition = projectPerformanceQueuePosition !== null ? projectPerformanceQueuePosition > index : false;
									return (
										<Table.Tr
											key={project.getId()}
											style={{
												backgroundColor: projectIsAtQueuePosition ? "lightgreen" : projectIsPastQueuePosition ? "gray" : ""
											}}
										>
											<Table.Td>{project.getName()}</Table.Td>
											<Table.Td>
												{project.getUser()?.username ?? ""}
											</Table.Td>
											<Table.Td>
												<Button
													size="xs"
													onClick={() => handleDownloadScoreParts(project)}
												>
													Download Parts
												</Button>
											</Table.Td>
										</Table.Tr>
									)
								})}
							</Table.Tbody>
						</Table>
					</ScrollArea>
				)}
			</Flex>
		</ConductionModal>
	);
};

export default LiveEventInstrumentalistInterface;
